import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
    authApi,
    productsApi,
    salesApi,
    posApi,
    reportsApi,
    cashApi,
    categoriesApi,
    contactsApi,
    conversationsApi,
    inventoryApi,
    discountEventsApi,
    suppliersApi,
    serializedInventoryApi,
    api
} from '@/lib/api';

type WarehouseOption = Awaited<ReturnType<typeof inventoryApi.getWarehouses>>[number];

const normalizeWarehouseOption = (
    warehouse: Partial<WarehouseOption> & {
        id?: string;
        name?: string;
        location?: string | null;
        store?: { id?: string; name?: string } | null;
        storeName?: string;
        storeId?: string | null;
    },
    fallbackStore?: { id?: string; name?: string },
): WarehouseOption | null => {
    if (!warehouse.id || !warehouse.name) {
        return null;
    }

    return {
        id: warehouse.id,
        name: warehouse.name,
        location: warehouse.location || '',
        type: warehouse.type,
        createdAt: warehouse.createdAt,
        storeId: warehouse.storeId ?? warehouse.store?.id ?? fallbackStore?.id ?? null,
        storeName: warehouse.storeName || warehouse.store?.name || fallbackStore?.name,
    };
};

const mergeWarehouseOptions = (...collections: Array<Array<WarehouseOption | null | undefined> | undefined>): WarehouseOption[] => {
    const merged = new Map<string, WarehouseOption>();

    for (const collection of collections) {
        for (const warehouse of collection || []) {
            if (!warehouse?.id) {
                continue;
            }

            const existing = merged.get(warehouse.id);
            merged.set(warehouse.id, {
                ...existing,
                ...warehouse,
                location: warehouse.location ?? existing?.location ?? '',
                storeId: warehouse.storeId ?? existing?.storeId ?? null,
                storeName: warehouse.storeName || existing?.storeName,
                type: warehouse.type || existing?.type,
                createdAt: warehouse.createdAt || existing?.createdAt,
            });
        }
    }

    return Array.from(merged.values());
};

const getWarehouseFallbackFromSession = (): WarehouseOption[] => {
    const user = api.getCurrentUser();
    const tenant = user?.tenant;

    const tenantWarehouses = (tenant?.warehouses || [])
        .map((warehouse) => normalizeWarehouseOption(warehouse))
        .filter(Boolean) as WarehouseOption[];

    const storeWarehouses = (tenant?.stores || [])
        .flatMap((store) => (store.warehouses || []).map((warehouse) => normalizeWarehouseOption(warehouse, store)))
        .filter(Boolean) as WarehouseOption[];

    const allowedWarehouses = (user?.allowedWarehouses || [])
        .map((warehouse) => normalizeWarehouseOption(warehouse))
        .filter(Boolean) as WarehouseOption[];

    return mergeWarehouseOptions(tenantWarehouses, storeWarehouses, allowedWarehouses);
};

// Auth Hooks
// Auth Hooks
export const useLogin = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ email, password }: { email: string; password: string }) =>
            authApi.login(email.trim().toLowerCase(), password),
        onSuccess: (data) => {
            // api.setAuth is already called inside authApi.login

            // Populate 'me' query cache immediately so UI is responsive
            if (data.user) {
                const tenantId = api.getTenantId();
                if (tenantId) {
                    queryClient.setQueryData(['me', tenantId], data.user);
                }
            }
            queryClient.invalidateQueries({ queryKey: ['auth'] });
        },
    });
};

export const useCurrentUser = () => {
    const tenantId = api.getTenantId();
    // Use sessionStorage cached user as fallback so HMR / backend-down
    // doesn't wipe access and show "Acceso Denegado"
    const cachedUser = api.getCurrentUser();
    return useQuery({
        queryKey: ['me', tenantId],
        queryFn: authApi.me,
        enabled: !!api.getToken() && !!tenantId,
        staleTime: 15 * 60 * 1000,
        refetchOnReconnect: true,
        retry: 2,
        retryDelay: 1500,
        placeholderData: cachedUser || undefined,
    });
};

export const useLogout = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: authApi.logout,
        onSuccess: () => {
            api.clearToken();
            queryClient.clear();
        },
    });
};



export const useAuthReady = () => {
    const token = api.getToken();
    const tenantId = api.getTenantId();
    const user = api.getCurrentUser(); // Fixed method name
    return {
        isReady: !!token && !!tenantId,
        token,
        tenantId,
        user
    };
};

// Products Hooks
export const useProducts = () => {
    const tenantId = api.getTenantId();
    const token = api.getToken();
    return useQuery({
        queryKey: ['products', tenantId],
        queryFn: productsApi.getAll,
        staleTime: 15 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnReconnect: false,
        enabled: !!token && !!tenantId,
    });
};

export const useProductsPaginated = (params: {
    q?: string;
    categoryId?: string;
    uncategorized?: boolean;
    includeArchived?: boolean;
    page?: number;
    limit?: number;
}) => {
    const tenantId = api.getTenantId();
    const token = api.getToken();
    return useQuery({
        queryKey: [
            'products-paginated',
            tenantId,
            params.page ?? 1,
            params.limit ?? 20,
            params.q ?? '',
            params.categoryId ?? '',
            params.uncategorized ?? false,
            params.includeArchived ?? false,
        ],
        queryFn: () => productsApi.getAllPaginated(params),
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        placeholderData: (prev: any) => prev,
        enabled: !!token && !!tenantId,
    });
};

export const useCategories = (filters?: { activeOnly?: boolean }) => {
    const tenantId = api.getTenantId();
    const token = api.getToken();

    return useQuery({
        queryKey: ['categories', tenantId, filters?.activeOnly ?? false],
        queryFn: () => categoriesApi.getAll(filters),
        staleTime: 15 * 60 * 1000,
        refetchOnReconnect: false,
        enabled: !!token && !!tenantId,
    });
};

// Sales Hooks
export const useSales = (page = 1) => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['sales', tenantId, page],
        queryFn: () => salesApi.getAll(page),
        placeholderData: keepPreviousData,
        enabled: !!api.getToken() && !!tenantId,
    });
};

export const useCreateSale = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: salesApi.create,
        onSuccess: (data) => {
            const tenantId = api.getTenantId();
            if (tenantId && data?.id) {
                queryClient.setQueryData(['sale', tenantId, data.id], data);
            }
            // Invalidate broadly as fallback, though optimistic updates should handle specific views
            queryClient.invalidateQueries({ queryKey: ['sales'] });
        },
    });
};

export const usePaySale = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ saleId, ...data }: { saleId: string; type: 'CASH' | 'CARD' | 'TRANSFER'; amount: number }) =>
            salesApi.pay(saleId, data),
        onSuccess: (data, variables) => {
            const tenantId = api.getTenantId();
            if (tenantId && variables?.saleId) {
                queryClient.setQueryData(['sale', tenantId, variables.saleId], data);
            }
            queryClient.invalidateQueries({ queryKey: ['sales'] });
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ['reports'] });
            }, 500);
        },
    });
};

export const useVoidSale = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ saleId, reason }: { saleId: string; reason: string }) =>
            posApi.voidSale(saleId, reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sales'] });
            queryClient.invalidateQueries({ queryKey: ['pos-void-return-requests'] });
            queryClient.invalidateQueries({ queryKey: ['internal-alerts'] });
            queryClient.invalidateQueries({ queryKey: ['internal-alert-count'] });
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ['notifications'] });
                queryClient.invalidateQueries({ queryKey: ['reports'] });
                queryClient.invalidateQueries({ queryKey: ['inventory'] });
                queryClient.invalidateQueries({ queryKey: ['inventory-balances'] });
                queryClient.invalidateQueries({ queryKey: ['pos-bootstrap'] });
                queryClient.invalidateQueries({ queryKey: ['negative-stock'] });
            }, 900);
        },
    });
};

export const useReturnSale = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ saleId, items, reason }: { saleId: string; items: Array<{ lineId: string; quantity: number; restock: boolean }>; reason?: string }) =>
            posApi.returnItems(saleId, { items, reason: reason || 'Devolucion POS' }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['sales'] });
            queryClient.invalidateQueries({ queryKey: ['pos-void-return-requests'] });
            queryClient.invalidateQueries({ queryKey: ['internal-alerts'] });
            queryClient.invalidateQueries({ queryKey: ['internal-alert-count'] });
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ['notifications'] });
                queryClient.invalidateQueries({ queryKey: ['reports'] });
                queryClient.invalidateQueries({ queryKey: ['inventory'] });
                queryClient.invalidateQueries({ queryKey: ['inventory-balances'] });
                queryClient.invalidateQueries({ queryKey: ['pos-bootstrap'] });
                queryClient.invalidateQueries({ queryKey: ['negative-stock'] });
            }, 900);
        },
    });
};

// Cash Hooks
export const useCashRegisters = (storeId?: string) => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['cashRegisters', tenantId, storeId],
        queryFn: () => cashApi.getRegisters(storeId),
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        enabled: !!api.getToken() && !!tenantId,
    });
};

export const useCurrentCashSession = (storeId?: string, registerId?: string) => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['cashSession', tenantId, storeId, registerId],
        queryFn: async () => {
            const response = await cashApi.getStatus(storeId, registerId);
            return response.session || null;
        },
        retry: false,
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        enabled: !!api.getToken() && !!tenantId,
    });
};

export const useOpenCashSession = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ registerId, startAmount, storeId }: { registerId: string; startAmount: number; storeId?: string }) =>
            cashApi.openSession(startAmount, registerId, storeId),
        onSuccess: (data, variables) => {
            const tenantId = api.getTenantId();
            if (tenantId) {
                queryClient.setQueryData(['cashSession', tenantId, variables.storeId, variables.registerId], data);
            }
            queryClient.invalidateQueries({ queryKey: ['cashSession'] });
            queryClient.invalidateQueries({ queryKey: ['cashRegisters'] });
            // POS reads cashSession from its own bootstrap cache (2min staleTime) to
            // decide whether checkout is blocked — without this, opening a turno from
            // Caja doesn't unblock an already-loaded POS tab until that cache expires.
            queryClient.invalidateQueries({ queryKey: ['pos-bootstrap'] });
        },
    });
};

export const useCloseCashSession = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ endAmount, registerId, notes, storeId }: { endAmount: number; registerId: string; notes?: string; storeId?: string }) =>
            cashApi.closeSession(endAmount, registerId, notes),
        onSuccess: (data, variables) => {
            const tenantId = api.getTenantId();
            if (tenantId) {
                queryClient.setQueryData(['cashSession', tenantId, variables.storeId, variables.registerId], data);
            }
            queryClient.invalidateQueries({ queryKey: ['cashSession'] });
            queryClient.invalidateQueries({ queryKey: ['cashRegisters'] });
            queryClient.invalidateQueries({ queryKey: ['pos-bootstrap'] });
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ['cashHistory'] });
            }, 500);
        },
    });
};

export const useCashMovement = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ type, amount, reason, registerId, description }: { type: string; amount: number; reason: string; registerId: string; description?: string }) =>
            cashApi.addMovement(type, amount, reason, registerId, description),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['cashSession'] });
        },
    });
};

export const useCashHistory = (opts?: { storeId?: string; registerId?: string; status?: string; page?: number }) => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['cashHistory', tenantId, opts?.page, opts?.storeId, opts?.registerId, opts?.status],
        queryFn: () => cashApi.getHistory(opts),
        staleTime: 30 * 1000,
        refetchOnWindowFocus: false,
        placeholderData: keepPreviousData,
        enabled: !!api.getToken() && !!tenantId,
    });
};

// Discount Events Hooks
export const useDiscountEvents = (opts?: { from?: string; to?: string; userId?: string; scope?: string; page?: number }) => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['discountEvents', tenantId, opts?.from, opts?.to, opts?.userId, opts?.scope, opts?.page],
        queryFn: () => discountEventsApi.getEvents(opts),
        enabled: !!api.getToken() && !!tenantId,
    });
};

// Contacts Hooks
export const useContacts = (page = 1) => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['contacts', tenantId, page],
        queryFn: () => contactsApi.getAll(page),
        enabled: !!api.getToken() && !!tenantId,
        placeholderData: keepPreviousData,
    });
};

// Conversations Hooks
export const useConversations = () => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['conversations', tenantId],
        queryFn: conversationsApi.getAll,
        enabled: !!api.getToken() && !!tenantId,
    });
};

export const useConversationMessages = (conversationId: string) => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['conversations', tenantId, conversationId],
        queryFn: () => conversationsApi.getMessages(conversationId),
        enabled: !!conversationId && !!api.getToken() && !!tenantId,
    });
};

export const useSendMessage = () => {
    const queryClient = useQueryClient();
    const tenantId = api.getTenantId();

    return useMutation({
        mutationFn: ({ conversationId, body }: { conversationId: string; body: string }) =>
            conversationsApi.sendMessage(conversationId, body),
        onSuccess: (_, { conversationId }) => {
            queryClient.invalidateQueries({ queryKey: ['conversations', tenantId, conversationId] });
        },
    });
};

// Inventory Hooks
export const useInventory = (
    warehouseId?: string,
    filters?: {
        search?: string;
        categoryId?: string;
        supplierId?: string;
        uncategorized?: boolean;
        consignment?: "all" | "consignment" | "owned";
    },
) => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: [
            'inventory',
            tenantId,
            warehouseId,
            filters?.search || '',
            filters?.categoryId || null,
            filters?.supplierId || null,
            filters?.uncategorized || false,
            filters?.consignment || 'all',
        ],
        queryFn: () => inventoryApi.getAvailability({
            warehouseId,
            search: filters?.search,
            categoryId: filters?.categoryId,
            supplierId: filters?.supplierId,
            uncategorized: filters?.uncategorized,
            consignment: filters?.consignment,
        }),
        staleTime: 60000,
        enabled: !!api.getToken() && !!tenantId,
    });
};

export const useInventoryItems = () => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['inventoryItems', tenantId],
        queryFn: inventoryApi.getItems,
        staleTime: 5 * 60 * 1000,
        enabled: !!api.getToken() && !!tenantId,
    });
};

export const useLowStock = () => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['inventory', 'low-stock', tenantId],
        queryFn: inventoryApi.getLowStock,
        enabled: !!api.getToken() && !!tenantId,
    });
};

export const useWarehouses = () => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['warehouses', tenantId],
        queryFn: async () => {
            const fallbackWarehouses = getWarehouseFallbackFromSession();

            try {
                const apiWarehouses = await inventoryApi.getWarehouses();
                return mergeWarehouseOptions(apiWarehouses, fallbackWarehouses);
            } catch (error) {
                if (fallbackWarehouses.length > 0) {
                    return fallbackWarehouses;
                }
                throw error;
            }
        },
        staleTime: 15 * 60 * 1000,
        gcTime: 15 * 60 * 1000,
        refetchOnReconnect: false,
        enabled: !!api.getToken() && !!tenantId,
    });
};

export const useReceiveBatch = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: inventoryApi.receiveBatch,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inventory'] });
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ['inventoryItems'] });
                queryClient.invalidateQueries({ queryKey: ['inventory-balances'] });
                queryClient.invalidateQueries({ queryKey: ['inventory-ledger'] });
                queryClient.invalidateQueries({ queryKey: ['pos-bootstrap'] });
                queryClient.invalidateQueries({ queryKey: ['negative-stock'] });
            }, 900);
        },
    });
};

export const useAdjustStock = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: inventoryApi.adjustStock,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['inventory'] });
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ['inventoryItems'] });
                queryClient.invalidateQueries({ queryKey: ['inventory-balances'] });
                queryClient.invalidateQueries({ queryKey: ['inventory-ledger'] });
                queryClient.invalidateQueries({ queryKey: ['pos-bootstrap'] });
                queryClient.invalidateQueries({ queryKey: ['negative-stock'] });
            }, 900);
        },
    });
};

// Dashboard Hook
export const useDashboard = () => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['dashboard', tenantId],
        queryFn: reportsApi.getDashboard,
        enabled: !!api.getToken() && !!tenantId,
    });
};

export const useProfitability = (from: string, to: string) => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['profitability', tenantId, from, to],
        queryFn: () => reportsApi.getProfitability(from, to),
        enabled: !!from && !!to && !!api.getToken() && !!tenantId,
    });
};

// Suppliers Hooks
export const useSuppliers = () => {
    const tenantId = api.getTenantId();
    return useQuery({
        queryKey: ['suppliers', tenantId],
        queryFn: () => suppliersApi.getAll(),
        staleTime: 15 * 60 * 1000,
        refetchOnReconnect: false,
        enabled: !!api.getToken() && !!tenantId,
    });
};

export const useCreateSupplier = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: { name: string; email?: string; phone?: string; address?: string }) =>
            suppliersApi.create(data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['suppliers'] });
        },
    });
};

export const useUpdateSupplier = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, ...data }: { id: string; name?: string; email?: string; phone?: string; address?: string }) =>
            suppliersApi.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['suppliers'] });
        },
    });
};

export const useDeleteSupplier = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => suppliersApi.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['suppliers'] });
        },
    });
};

// Reports Hooks with proper guards
export const useReportsSummary = (from: string, to: string) => {
    const token = api.getToken();
    const tenantId = api.getTenantId();
    const hasAuth = !!token && !!tenantId;

    return useQuery({
        queryKey: ['reports', 'summary', tenantId, from, to],
        queryFn: () => reportsApi.getSummary(from, to),
        enabled: hasAuth && !!from && !!to,
        retry: false,
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        meta: { noRetry404: true }
    });
};

export const useReportsByProduct = (from: string, to: string) => {
    const token = api.getToken();
    const tenantId = api.getTenantId();
    const hasAuth = !!token && !!tenantId;

    return useQuery({
        queryKey: ['reports', 'byProduct', tenantId, from, to],
        queryFn: () => reportsApi.getByProduct(from, to),
        enabled: hasAuth && !!from && !!to,
        retry: false,
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        meta: { noRetry404: true }
    });
};

export const useImeiSearch = (imei: string, enabled = true) => {
    const tenantId = api.getTenantId();
    const normalized = imei.trim();
    return useQuery({
        queryKey: ['imei-search', tenantId, normalized],
        queryFn: () => serializedInventoryApi.searchByImei(normalized).then((r) => r ?? null),
        enabled: enabled && !!api.getToken() && !!tenantId && normalized.length >= 4,
        staleTime: 30_000,
        retry: false,
    });
};
